import {
  Award,
  Globe,
  Handshake,
  Heart,
  Leaf,
  Lightbulb,
  MapPin,
  Plane,
  Rocket,
  Search,
  ShieldCheck,
  Smile,
  Sparkles,
  Star,
  Target,
  TrendingUp,
  Trophy,
  Users,
  type LucideIcon,
} from 'lucide-react';
import React from 'react';

import type { WelcomeIconKey, WelcomePlatform } from '@utazzo/shared';

const LUCIDE: Partial<Record<WelcomeIconKey, LucideIcon>> = {
  'trending-up': TrendingUp,
  award: Award,
  users: Users,
  globe: Globe,
  trophy: Trophy,
  star: Star,
  heart: Heart,
  plane: Plane,
  search: Search,
  sparkles: Sparkles,
  'shield-check': ShieldCheck,
  lightbulb: Lightbulb,
  leaf: Leaf,
  target: Target,
  handshake: Handshake,
  rocket: Rocket,
  'map-pin': MapPin,
  smile: Smile,
};

// lucide dropped brand marks, so the social glyphs are drawn inline.
const BRAND_PATHS: Record<'google' | 'facebook' | 'instagram' | 'youtube' | 'whatsapp', string> = {
  google:
    'M21.35 11.1H12v2.98h5.35c-.23 1.4-1.64 4.1-5.35 4.1-3.22 0-5.85-2.67-5.85-5.96S8.78 6.26 12 6.26c1.83 0 3.06.78 3.76 1.45l2.56-2.47C16.68 3.7 14.54 2.75 12 2.75 6.9 2.75 2.75 6.9 2.75 12S6.9 21.25 12 21.25c5.34 0 8.88-3.75 8.88-9.04 0-.6-.07-1.07-.15-1.11Z',
  facebook:
    'M13.5 21.5v-7.6h2.56l.38-2.97H13.5V9.04c0-.86.24-1.45 1.47-1.45h1.57V4.93a21 21 0 0 0-2.29-.12c-2.27 0-3.82 1.39-3.82 3.93v2.19H7.87v2.97h2.56v7.6h3.07Z',
  instagram:
    'M12 7.3A4.7 4.7 0 1 0 12 16.7 4.7 4.7 0 0 0 12 7.3Zm0 7.75A3.05 3.05 0 1 1 12 8.95a3.05 3.05 0 0 1 0 6.1Zm4.9-8.95a1.1 1.1 0 1 0 0 2.2 1.1 1.1 0 0 0 0-2.2ZM12 3c-2.44 0-2.75.01-3.71.05-3.26.15-5.09 1.97-5.24 5.24C3.01 9.25 3 9.56 3 12s.01 2.75.05 3.71c.15 3.26 1.97 5.09 5.24 5.24.96.04 1.27.05 3.71.05s2.75-.01 3.71-.05c3.26-.15 5.09-1.97 5.24-5.24.04-.96.05-1.27.05-3.71s-.01-2.75-.05-3.71c-.15-3.26-1.97-5.09-5.24-5.24C14.75 3.01 14.44 3 12 3Zm0 1.62c2.4 0 2.69.01 3.64.05 2.46.11 3.6 1.28 3.71 3.71.04.95.05 1.24.05 3.64s-.01 2.69-.05 3.64c-.11 2.43-1.25 3.6-3.71 3.71-.95.04-1.23.05-3.64.05s-2.69-.01-3.64-.05c-2.46-.11-3.6-1.28-3.71-3.71-.04-.95-.05-1.24-.05-3.64s.01-2.69.05-3.64c.11-2.43 1.25-3.6 3.71-3.71.95-.04 1.24-.05 3.64-.05Z',
  youtube:
    'M21.58 7.19a2.5 2.5 0 0 0-1.76-1.77C18.25 5 12 5 12 5s-6.25 0-7.82.42a2.5 2.5 0 0 0-1.76 1.77C2 8.77 2 12 2 12s0 3.23.42 4.81a2.5 2.5 0 0 0 1.76 1.77C5.75 19 12 19 12 19s6.25 0 7.82-.42a2.5 2.5 0 0 0 1.76-1.77C22 15.23 22 12 22 12s0-3.23-.42-4.81ZM10 15V9l5.2 3L10 15Z',
  whatsapp:
    'M12.04 2.5a9.44 9.44 0 0 0-8.1 14.28L2.5 21.5l4.85-1.4a9.44 9.44 0 1 0 4.69-17.6Zm0 17.27a7.8 7.8 0 0 1-4-1.1l-.28-.17-2.88.83.85-2.8-.19-.3a7.82 7.82 0 1 1 6.5 3.54Zm4.3-5.85c-.24-.12-1.4-.69-1.61-.77-.22-.08-.38-.12-.54.12-.16.24-.62.77-.76.93-.14.16-.28.18-.52.06a6.4 6.4 0 0 1-3.2-2.8c-.24-.41.24-.38.69-1.27.08-.16.04-.3-.02-.42-.06-.12-.54-1.3-.74-1.78-.2-.47-.4-.4-.54-.41h-.46a.88.88 0 0 0-.64.3 2.68 2.68 0 0 0-.84 2c0 1.18.86 2.32.98 2.48.12.16 1.69 2.58 4.1 3.62 1.52.66 2.12.72 2.88.6.46-.07 1.4-.57 1.6-1.13.2-.56.2-1.03.14-1.13-.06-.1-.22-.16-.46-.28Z',
};

type BrandKey = keyof typeof BRAND_PATHS;
const isBrand = (k: string): k is BrandKey => k in BRAND_PATHS;

/** Renders a stat / value / platform icon by key. */
export const WelcomeIcon = ({
  name,
  className,
  strokeWidth = 1.75,
}: {
  name: WelcomeIconKey | WelcomePlatform;
  className?: string;
  strokeWidth?: number;
}): React.JSX.Element => {
  if (isBrand(name)) {
    return (
      <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden className={className}>
        <path d={BRAND_PATHS[name]} />
      </svg>
    );
  }
  const Icon = LUCIDE[name] ?? Star;
  return <Icon className={className} strokeWidth={strokeWidth} aria-hidden />;
};
